import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useLearningProgress } from "../contexts/AuthContext";
import { Role } from "../constants/Role";
import {
  ReadOutlined,
  LogoutOutlined,
  ProfileOutlined,
  ShoppingCartOutlined,
  LoginOutlined,
  UserOutlined,
  SearchOutlined,
  WalletOutlined,
  TrophyOutlined,
  CheckCircleFilled
} from "@ant-design/icons";
import { Menu, Dropdown, Divider, Progress, Popover, Button, message } from "antd";
import { debounce } from "../utils/debounce";
import LearnerCourseService from "../services/LearnerCourseService";
import CartApiService from '../services/CartApiService';
import CartDropdown from './ui/CartDropdown';
import LearnerSearchBar from "./LearnerSearchBar";
import KeycloakService from "../services/KeycloakService";
import { useRef } from "react";
/**
 * LearnerNavbar component for the application
 */
const LearnerNavbar = () => {
  const { authenticated, hasRole, hasAnyRole, login, logout, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { progress } = useLearningProgress();
  const [cartCount, setCartCount] = React.useState(0);
  const [certPopoverVisible, setCertPopoverVisible] = React.useState(false);
  const [certLoading, setCertLoading] = React.useState(false);
  const [certError, setCertError] = React.useState("");
  const [certBtnHoverHeader, setCertBtnHoverHeader] = React.useState(false);
  const [certBtnHoverPopover, setCertBtnHoverPopover] = React.useState(false);
  const certBtnTextColorHeader = '#2056ac';
  const certBtnBgColorHeader = certBtnHoverHeader ? '#f5f5f5' : '#fff';
  const certBtnIconColorHeader = '#2056ac';
  // Popup button hover style
  const certBtnBgColorPopover = certBtnHoverPopover ? '#f5f5f5' : '#fff';
  const certBtnTextColorPopover = '#2056ac';
  const certBtnIconColorPopover = '#2056ac';

  // Lấy số lượng cart khi mount và khi route thay đổi
  React.useEffect(() => {
    if (!authenticated) {
      setCartCount(0);
      return;
    }
    // Chỉ fetch cart count cho role LEARNER
    if (!hasAnyRole([Role.LEARNER])) {
      setCartCount(0);
      return;
    }
    const fetchCartCount = async () => {
      try {
        const res = await CartApiService.getCartCount();
        setCartCount(res.data?.count || 0);
      } catch {
        setCartCount(0);
      }
    };
    fetchCartCount();
  }, [location, authenticated, hasAnyRole]);

  // Lắng nghe custom event để cập nhật cart count khi add/remove
  React.useEffect(() => {
    const handleCartChanged = () => {
      // Chỉ cập nhật cart count cho role LEARNER
      if (!hasAnyRole([Role.LEARNER])) {
        setCartCount(0);
        return;
      }
      CartApiService.getCartCount().then(res => setCartCount(res.data?.count || 0));
    };
    window.addEventListener('cart-changed', handleCartChanged);
    return () => window.removeEventListener('cart-changed', handleCartChanged);
  }, [hasAnyRole]);

  // Handle login button click
  const handleLogin = () => {
    login();
  };

  // Handle My Learning click - redirect to Keycloak with homepage as redirect
  const handleMyLearningClick = (e) => {
    e.preventDefault();
    if (!authenticated) {
      // Redirect to Keycloak with homepage as the redirect URL
      KeycloakService.login('/');
    } else {
      // If already authenticated, navigate normally
      navigate('/my-courses');
    }
  };

  // Handle logout button click
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Handle profile click - redirect to Keycloak account management in new tab
  const handleProfileClick = () => {
    const keycloak = KeycloakService.getKeycloak();
    const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'https://sso.jetskill.net';
    const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'online-learning';
    const accountUrl = `${keycloakUrl}/realms/${realm}/account`;
    
    // Always open in new tab for better user experience
    window.open(accountUrl, '_blank');
  };

  const isLearnCoursePage = location.pathname.startsWith('/learner/my-courses');
  const isMyCoursesPage = location.pathname === '/my-courses';
  // Check role mentor 
  const isMentor = hasRole && hasRole(Role.STAFF_COURSE_MENTOR);

  // Extract courseSlug if on learn course page
  let courseSlug = null;
  if (isLearnCoursePage) {
    const match = location.pathname.match(/\/learner\/my-courses\/([^/]+)/);
    if (match) courseSlug = match[1];
  }

  const progressPercent = progress.totalLessons ? Math.round((progress.completedLessons / progress.totalLessons) * 100) : 0;
  const isCourseCompleted = isLearnCoursePage && progressPercent === 100 && courseSlug;

  const handleGetCertificate = async () => {
    if (!courseSlug) return;
    setCertLoading(true);
    setCertError("");
    try {
      const data = await LearnerCourseService.getCertificate(courseSlug);
      if (data && data.certificateUrl) {
        window.open(data.certificateUrl, "_blank");
        setCertPopoverVisible(false);
      } else {
        setCertError("Certificate not found.");
      }
    } catch (e) {
      setCertError(e?.message || "Could not get certificate");
    }
    setCertLoading(false);
  };

  const userMenu = (
    <div style={{ padding: "12px", width: "200px" }}>
      <Menu>
        <Menu.Item>
          <div style={{ fontWeight: 600, color: "Gray" }}>
            {authenticated && userProfile && (
              <div style={{ fontSize: "15px" }}>
                <span>
                  {userProfile?.firstName || userProfile?.given_name}{" "}
                  {userProfile?.lastName || userProfile?.family_name}
                </span>
              </div>
            )}
          </div>
        </Menu.Item>
        <Divider style={{ margin: "12px 0" }} />
        <Menu.Item
          key="profile"
          icon={<ProfileOutlined />}
          onClick={handleProfileClick}
        >
          Profile
        </Menu.Item>
        <Menu.Item
          key="order-history"
          icon={<WalletOutlined />}
          onClick={() => navigate("/order-history")}
        >
          Order History
        </Menu.Item>
        <Menu.Item
          key="logout"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
        >
          Logout
        </Menu.Item>
      </Menu>
    </div>
  );

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <Link to="/" className="navbar-logo">
          <ReadOutlined /> JetSkill
        </Link>
      </div>

      <div className="navbar-menu">
        <div className="navbar-start">
          <Link to="/" className="navbar-item">
            Home
          </Link>

          <Link to="/all-courses" className="navbar-item">
            All Courses
          </Link>

          {authenticated && (
            <a href="/my-courses" className="navbar-item" onClick={handleMyLearningClick}>
              My Learning
            </a>
          )}

          <Link to="/about" className="navbar-item">
            About Us
          </Link>
        </div>

        <div className="navbar-end">
          {isCourseCompleted ? (
            <Popover
              overlayInnerStyle={{
                background: '#fff',
                borderRadius: 16,
                padding: '16px 35px',
                
                // minWidth: 260,
                width:'100%',
                boxShadow: '0 4px 24px #0001',
                boxSizing: 'border-box',
              }}
              content={
                <div style={{ width: '100%' }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#2056ac", marginBottom: 8 }}>
                    <CheckCircleFilled style={{ color: "#52c41a", marginRight: 6 }} />
                    Completed {progress.completedLessons}/{progress.totalLessons}
                  </div>
                  <Button
                    type="primary"
                    icon={<TrophyOutlined style={{ color: certBtnIconColorPopover }} />}
                    loading={certLoading}
                    style={{
                      background: certBtnBgColorPopover,
                      color: certBtnTextColorPopover,
                      border: "2px solid #2056ac",
                      fontWeight: 500,
                      fontSize: 16,
                      width: "100%",
                      margin: "12px 0",
                      borderRadius: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      boxShadow: certBtnHoverPopover ? '0 2px 8px #2056ac22' : undefined,
                    }}
                    onClick={handleGetCertificate}
                    disabled={certLoading}
                    onMouseEnter={() => setCertBtnHoverPopover(true)}
                    onMouseLeave={() => setCertBtnHoverPopover(false)}
                  >
                    <span style={{ color: certBtnTextColorPopover, fontWeight: 600 }}>Get here</span>
                  </Button>
                  {certError && <div style={{ color: "red", marginTop: 8 }}>{certError}</div>}
                </div>
              }
              title={null}
              trigger="click"
              open={certPopoverVisible}
              onOpenChange={setCertPopoverVisible}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 12,
                  cursor: 'pointer',
                  background: certBtnBgColorHeader,
                  borderRadius: 24,
                  padding: '4px 18px',
                  boxShadow: certBtnHoverHeader ? '0 2px 8px #2056ac22' : undefined,
                  border: '2px solid #2056ac',
                  transition: 'all 0.2s',
                  color: certBtnTextColorHeader,
                  fontWeight: 500,
                  fontSize: 15,
                  userSelect: 'none',
                  gap: 10,
                }}
                onMouseEnter={() => setCertBtnHoverHeader(true)}
                onMouseLeave={() => setCertBtnHoverHeader(false)}
              >
                <div style={{ position: 'relative', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Progress
                    type="circle"
                    percent={100}
                    width={32}
                    strokeWidth={10}
                    strokeColor={certBtnIconColorHeader}
                    trailColor="#e6e6e6"
                    format={() => null}
                    style={{ position: 'absolute', top: 0, left: 0 }}
                  />
                  <TrophyOutlined
                    style={{
                      color: certBtnIconColorHeader,
                      fontSize: 18,
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                </div>
                <span style={{ color: certBtnTextColorHeader, fontWeight: 700, fontSize: 15, transition: 'color 0.2s' }}>
                  Get Certificate
                </span>
              </div>
            </Popover>
          ) : isLearnCoursePage && progress.totalLessons > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', marginRight: 12 }}>
              <span style={{ color: '#fff', fontWeight: 500, fontSize: 15, marginRight: 16, letterSpacing: 0.2 }}>Your progress</span>
              <Progress
                type="circle"
                percent={progressPercent}
                width={40}
                strokeWidth={8}
                strokeColor="#fff"
                trailColor="#b3c6e6"
                format={percent => (
                  <span style={{ color: "#fff", fontWeight: "bold" }}>
                    {percent}%
                  </span>
                )}
              />
            </div>
          ) : !isMyCoursesPage ? (
            <LearnerSearchBar />
          ) : null}
          {authenticated ? (
            <div
              className="navbar-item user-info"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "32px",
              }}
            >
              <div className="navbar-cart-wrapper" style={{ position: 'relative' }}>
                <Dropdown
                  overlay={<CartDropdown />}
                  trigger={["hover"]}
                  placement="bottomRight"
                  overlayClassName="cart-dropdown-overlay"
                >
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    <ShoppingCartOutlined
                      className="navbar-icon navbar-cart"
                     
                      onClick={() => navigate("/cart")}
                    />
                    {cartCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: -9,
                          right: -10,
                          background: '#fff',
                          color: 'black',
                          borderRadius: '50%',
                          minWidth: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 11,
                          border: '2px solid #fff',
                          zIndex: 2
                        }}
                      >
                        {cartCount}
                      </span>
                    )}
                  </span>
                </Dropdown>
              </div>
              <div className="navbar-user-wrapper">
                <Dropdown
                  overlay={userMenu}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <UserOutlined className="navbar-icon" style={{ cursor: "pointer" }} />
                </Dropdown>
              </div>
            </div>
          ) : (
            <button
              className="login-button"
              icon={<LoginOutlined />}
              onClick={handleLogin}
            >
              Login
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default LearnerNavbar;
